import React, { useEffect, useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import moment from 'moment';
import { getApi } from 'redux/sagas/getApiDataSaga';
import { SERVER_IP } from 'assets/Config';
import { ACTIONS, API_STATUS, DATE_FORMAT } from 'constants/app-constants';
import { objToQs } from 'helpers';
import DispatchReportListPresentational from './dispatch-report-list-presentational';
import { dispatchColumn } from '..';

const initialPageSize = 10;
const intialPageSizeOptions = [10, 15, 20];

const DispatchReportListFunctional = React.memo((props) => {
	const column = dispatchColumn;
	const [currentPage, setCurrentPage] = useState(1);
	const [pageSize, setPageSize] = useState(initialPageSize);
	const [filterOptions, setFilterOptions] = useState({
		fromDate: moment().subtract(2, 'days').format(DATE_FORMAT.YYYY_MM_DD),
		toDate: moment().format(DATE_FORMAT.YYYY_MM_DD),
		searchKey: '',
	});
	const globalRedux = useSelector((state) => state.globalRedux);
	const dispatchReports = useSelector((state) => state.dispatchReportsRedux?.dispatchReports);
	const [tableData, setTableData] = useState(dispatchReports || []);
	const dispatch = useDispatch();

	const getDispatchReportList = () => {
		// Ensure that fromDate and toDate are not empty or invalid
		if (!filterOptions.fromDate || !filterOptions.toDate || !moment(filterOptions.fromDate, DATE_FORMAT.YYYY_MM_DD, true).isValid() || !moment(filterOptions.toDate, DATE_FORMAT.YYYY_MM_DD, true).isValid()) {
			console.log('Invalid or empty date range. API call aborted.');
			return; // Exit if no valid dates are provided
		}
	
		// Ensure that the dates are formatted as YYYY_MM_DD
		const formattedFilterOptions = {
			...filterOptions,
			fromDate: moment(filterOptions.fromDate).format(DATE_FORMAT.YYYY_MM_DD),
			toDate: moment(filterOptions.toDate).format(DATE_FORMAT.YYYY_MM_DD),
		};
	
		// Build the API params
		let params = objToQs({
			orgId: globalRedux.selectedOrganization._id,
			...formattedFilterOptions, // use the formatted dates in the API request
		});
	
		// Dispatch the API action
		dispatch(getApi(ACTIONS.GET_DISPATCH_REPORTS, `${SERVER_IP}dispatch/report?${params}`));
	};
	
	// In the component where you're handling the date range picker clearing:
	
	const handleDateChange = (dates) => {
		if (!dates || dates.length === 0) {
			// If cleared, reset to default or empty values
			setFilterOptions({
				fromDate: moment().subtract(2, 'days').format(DATE_FORMAT.YYYY_MM_DD), // default to 2 days ago or empty
				toDate: moment().format(DATE_FORMAT.YYYY_MM_DD), // default to today
			});
		} else {
			// Set the selected dates
			setFilterOptions({
				fromDate: moment(dates[0]).format(DATE_FORMAT.YYYY_MM_DD),
				toDate: moment(dates[1]).format(DATE_FORMAT.YYYY_MM_DD),
			});
		}
	};

	// useEffect(() => {
	// 	getDispatchReportList();
	// }, [filterOptions]); // re-fetch when filter options change

	useEffect(() => {
		setTableData(dispatchReports);
	}, [dispatchReports]);
		console.log("🚀 ~ useEffect ~ dispatchReports:", dispatchReports)

	const tableLoading = useMemo(() => globalRedux.apiStatus.GET_DISPATCH_REPORTS === API_STATUS.PENDING, [globalRedux.apiStatus]);

	const handleTableChange = (currentPage, pageSize) => {
		setCurrentPage(currentPage === 0 ? 1 : currentPage);
		setPageSize(pageSize);
	};

	const getStartingValue = () => {
		if (currentPage === 1) return 1;
		else {
			return (currentPage - 1) * pageSize + 1;
		}
	};

	const getEndingValue = () => {
		if (currentPage === 1) return tableData.length < pageSize ? tableData.length : pageSize;
		else {
			let end = currentPage * pageSize;
			return end > tableData.length ? tableData.length : end;
		}
	};

	const exportData = useMemo(() => {
		let totalInvoiceWeight = 0;
		let totalInvoiceQty = 0;
		let totalTaxValue = 0;
		let totalValue = 0;

		tableData.forEach(({ invoiceWeight, invoiceQty, taxValue, totalValue: tValue }) => {
			totalInvoiceWeight += invoiceWeight || 0;
			totalInvoiceQty += invoiceQty || 0;
			totalTaxValue += taxValue || 0;
			totalValue += tValue || 0;
		});

		const data = {
			headers: column?.map((col) => col?.title) || [],
			data: [
				...(tableData.map((record) => ({
					[column[0]?.title]: record?.loadNo,
					[column[1]?.title]: moment(record?.loadDate).format(DATE_FORMAT.DD_MM_YYYY),
					[column[2]?.title]: record?.vehicleNo,
					[column[3]?.title]: record?.lrNumber,
					[column[4]?.title]: record?.partNumber,
					[column[5]?.title]: record?.sapNumber,
					[column[6]?.title]: record?.invoiceNumber,
					[column[7]?.title]: record?.invoiceQty,
					[column[8]?.title]: record?.invoiceWeight,
					[column[9]?.title]: parseFloat(record?.taxValue || 0).toFixed(2),
					[column[10]?.title]: parseFloat(record?.totalValue || 0).toFixed(2),
					[column[11]?.title]: record?.status,
					[column[12]?.title]: moment(record?.dispatchDate).format(DATE_FORMAT.DD_MM_YYYY),
				})) || []),
			],
		};

		// Add total row if data exists
		if (tableData?.length > 0) {
			data.data.push({
				[column[0]?.title]: 'Total',
				[column[1]?.title]: '',
				[column[2]?.title]: '',
				[column[3]?.title]: '',
				[column[4]?.title]: '',
				[column[5]?.title]: '',
				[column[6]?.title]: '',
				[column[7]?.title]: totalInvoiceQty,
				[column[8]?.title]: totalInvoiceWeight,
				[column[9]?.title]: parseFloat(totalTaxValue).toFixed(2),
				[column[10]?.title]: parseFloat(totalValue).toFixed(2),
				[column[11]?.title]: '',
				[column[12]?.title]: '',
			});
		}

		return data;
	}, [tableData, column]);

	return (
		<DispatchReportListPresentational
			{...{
				column,
				tableData,
				handleTableChange,
				getStartingValue,
				getEndingValue,
				pageSize,
				intialPageSizeOptions,
				initialPageSize,
				currentPage,
				tableLoading,
				exportData,
				filterOptions, 
				setFilterOptions, // Passing function to update filter options
				filterOptions, // Passing current filter options for UI handling
				handleDateChange,
				getDispatchReportList,
			}}
		/>
	);
});

export default DispatchReportListFunctional;
